import React, { useCallback } from 'react';
import {
  Grid, Box, BoxProps, Link, CircularProgress, styled,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ErrorCard } from '../shared/ErrorCard';
import { ContainerFrame } from '../frame';
import RateCard from './RateCard';
import { intlDateFormat } from '../../constants';
import environment from '../../environments/environment';
import {
  BillEstimateResponse, IAccountReduxState, IAppReduxState, ServicePoint,
} from '../../models';

const RateCardBox = styled(Box)<BoxProps>(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
}));

interface RatePlanOptionProps {
  billEstimateResponse: BillEstimateResponse[] | undefined;
  selectedServicePoint: ServicePoint | undefined;
  isLoading: boolean;
  errorCode?: string;
}

/**
 * Function to render Account information with cards
 */
function RatePlanOption({
  billEstimateResponse, selectedServicePoint, isLoading, errorCode,
}: RatePlanOptionProps) {
  const intl = useIntl();

  const account = useSelector<IAppReduxState, IAccountReduxState>((state) => state.account);

  function getLink(chunks: any) {
    return <Link target="_blank" rel="noopener noreferrer" href={environment.fixedBillPlanURL}>{chunks}</Link>;
  }

  const isCurrentPlan = useCallback(
    (cardContent: BillEstimateResponse) => selectedServicePoint?.currentPlanCode === cardContent.planCode,
    [selectedServicePoint?.currentPlanCode],
  );

  const isSwitchAllowed = useCallback(
    (cardContent: BillEstimateResponse) => !(
      selectedServicePoint?.currentPlanCode !== cardContent.planCode && !cardContent.isSwitchAllowed),
    [selectedServicePoint?.currentPlanCode],
  );

  return (
    <>
      <Box>
        {!isLoading && errorCode && (
          <RateCardBox>
            <Grid container direction="column" alignItems="center">
              <Grid item xs={12} sm={6} md={12} sx={{ marginTop: { xs: -45, sm: -40 } }}>
                <ErrorCard errorImage={<ErrorOutlineIcon color="error" sx={{ fontSize: 50 }} />}>
                  <FormattedMessage
                    id={errorCode}
                    values={{
                      sp_date: intl.formatDate(selectedServicePoint?.serviceEffectiveDate, intlDateFormat),
                      switch_date: intl.formatDate(selectedServicePoint?.switchRequestDate, intlDateFormat),
                      link: (chunks: any) => getLink(chunks),
                      currentRatePlan: selectedServicePoint?.currentPlanCode,
                    }}
                  />
                </ErrorCard>
              </Grid>
            </Grid>
          </RateCardBox>
        )}
      </Box>
      {isLoading && (
        <ContainerFrame>
          <Grid
            container
            sx={{ marginTop: -35 }}
          >
            <Grid
              item
              display="flex"
              justifyContent="center"
              md
              sm
              xs
            >
              <CircularProgress color="info" size="50px" title="Loading bill estimation data..." />
            </Grid>
          </Grid>
        </ContainerFrame>
      )}
      {!isLoading && !errorCode && (
        <ContainerFrame>
          <Grid
            container
            spacing={1}
            direction={{
              xs: 'column', md: 'row', sm: 'column', lg: 'row',
            }}
            justifyContent="center"
            sx={{ marginTop: { xs: -49, sm: -40 } }}
            id="RateCardContainer"
          >
            {account && billEstimateResponse && billEstimateResponse.map((cardContent) => (
              isSwitchAllowed(cardContent)
              && (
                <Grid
                  key={cardContent.planCode}
                  item
                  display="flex"
                  justifyContent="center"
                  md
                  sm
                >
                  <RateCard
                    billEstimateResponse={cardContent}
                    isCurrentPlan={isCurrentPlan(cardContent)}
                  />
                </Grid>
              )
            ))}
          </Grid>
        </ContainerFrame>
      )}
    </>
  );
}

RatePlanOption.defaultProps = {
  errorCode: '',
};

export default RatePlanOption;
